import React, { Suspense, useEffect, useState } from 'react'
import pclnExpt from '@pcln/experimentation'
import { useSelector } from 'react-redux'
import { Left, ListingsBody, Middle } from './ListingsPage.styles'
import { SponsoredFilter } from '../SponsoredFilter'
import { ListingsContentContainer } from '../../containers/listings-content-container'
import MediaQueryMatch from '@pcln/media-query-match'
import ClientOnly from '../../../common-components/ClientOnly'
import { AdContainer } from '../../containers/ad-container'
import { BestPriceGuarantee } from '../BestPriceGuarantee'
import { LeftFilterSkeleton } from '../LeftFilterSkeleton/LeftFilterSkeleton'
import { Loader } from '../Loader'
import AirSearchPromiseResolver from '../AirSearchPromiseResolver/AirSearchPromiseResolver'
import { usePartner } from '@pcln/whitelabel-components'
import { useFeature } from '@pcln/feature-components'
import { isServerSide } from '../../util/isServerSide'
import { PricePredictionContainer } from '../PriceWatch/PricePredictionContainer'
import { ToastProvider } from 'pcln-design-system'
import { Filters } from '../Filters'
import { MediaQuery } from 'react-responsive'
import { flyBreakPoints } from '@mfs/client/common/getTheme'
import { useIsMobile } from '../../helpers/useIsMobile'
import { ExpressDealFiltersContainer } from '@mfs/client/listings-client/components/ExpressDealFiltersBox'
import { productMap } from '@mfs/client/common/constants/constants'
import { logEvent } from '@mfs/client/common/log-ga-event/logGaEvent'

const ListingsPageV2 = ({
  pclnBootstrapData,
  setSelectedDepartureSummaries,
  selectedDepartureSummaries,
  handleTabChange,
  theme,
  forceHeaderOpen,
  variantLoader,
}) => {
  const { isReshopExperience } = useSelector((state) => state.pclnBootstrapData) || {}
  const partner = usePartner()
  const displayAds = useSelector(
    (state) =>
      ((partner && state.pclnBootstrapData?.features?.whiteLabel?.adsEnabled) || !partner) &&
      state?.uiToggles?.displayAds
  )
  const isMobileFromServer = useSelector((state) => state?.pclnBootstrapData?.isMobile)
  const isMobileFromClient = useIsMobile()
  const isMobile = isServerSide ? isMobileFromServer : isMobileFromClient
  // The BPG card should not display if toggled off in launch darkly,
  // if no listings were retrieved, or if the user is in a reshop experience
  const showBPGCard = useSelector(
    (state) =>
      state.pclnBootstrapData?.features?.whiteLabel?.bestPriceGuaranteeEnabled &&
      state.fetchedInitialListings &&
      !state.pclnBootstrapData?.isReshopExperience &&
      !state.pclnBootstrapData?.isPackageListings
  )

  const hasExpressDeal = useSelector((state) => state.flags?.hasExpressDeal)

  const ahCrossSellMaxShowCount = useFeature('flights.ahCrossSellMaxShowCount', 3)

  const isPackageListings = useSelector((state) => state?.pclnBootstrapData?.isPackageListings)
  const isHorizonListing = useSelector((state) => state?.pclnBootstrapData?.isHorizonListing)
  const bundleComponents = useSelector((state) => state?.pclnBootstrapData?.bundleComponents || [])
  const listingsLoadingMessage = useSelector((state) => state.listingsLoadingMessage)

  const packageType = isPackageListings
    ? bundleComponents?.map((component) => productMap[component.type])?.join(' + ') || ''
    : ''

  const showSponsoredFilter = !isReshopExperience && !isPackageListings

  const [priceWatchState, setPriceWatchState] = useState(null)

  const isExdCardVariant = pclnExpt.isVariant('AIR_DESKTOP_LISTINGS_EXD_BANNER_UI')

  useEffect(() => {
    pclnExpt.fireImpression('CG_DIGIOH_FLIGHT_LISTING')
    pclnExpt.fireImpression('CG_FL_1X1_INLINE')
  }, [])

  const showDesktopExdBanners = !isMobile && !isReshopExperience && hasExpressDeal

  useEffect(() => {
    if (showDesktopExdBanners) {
      pclnExpt.fireImpression('AIR_DESKTOP_LISTINGS_EXD_BANNER_UI')
    }
  }, [showDesktopExdBanners])

  useEffect(() => {
    if (showDesktopExdBanners && isExdCardVariant) {
      logEvent('display', {
        type: 'what_are_exds_banner',
        page_name: 'listings',
        product_name: 'air',
      })
    }
  }, [showDesktopExdBanners, isExdCardVariant])

  const renderPriceWatch = () => {
    if (!isMobile && priceWatchState !== null) {
      return (
        <ToastProvider domRootId='listing-toast-container' theme={theme} enterAnimation='slideInRight'>
          <PricePredictionContainer appCode='DESKTOP' {...priceWatchState} isMobile={!isMobile} />
        </ToastProvider>
      )
    }
  }

  return (
    <ListingsBody mt={[2, null, null, null, 4]}>
      <MediaQuery minWidth={flyBreakPoints.desktop}>
        <Left>
          <div
            style={{ position: 'fixed', bottom: '-112px', right: '174px', zIndex: 500 }}
            id='listing-toast-container'
          ></div>
          {renderPriceWatch()}
          <Suspense fallback={<LeftFilterSkeleton />}>
            <AirSearchPromiseResolver>
              {showSponsoredFilter ? <SponsoredFilter pclnBootstrapData={pclnBootstrapData} /> : null}
              {!isReshopExperience && hasExpressDeal && (
                <ExpressDealFiltersContainer isExdCardVariant={isExdCardVariant} />
              )}
              <Filters />
              {showBPGCard && (
                <MediaQueryMatch minWidth={'md'}>
                  <BestPriceGuarantee />
                </MediaQueryMatch>
              )}
            </AirSearchPromiseResolver>
          </Suspense>
          {!isPackageListings && (
            <ClientOnly>
              <AdContainer adId={'div-gpt-ad-tower'} isSticky={true} minScreenSize={'desktop'} />
            </ClientOnly>
          )}
        </Left>
      </MediaQuery>
      <Middle displayAds={displayAds}>
        <Suspense
          fallback={
            <Loader
              variantLoader={variantLoader}
              searchRequest={pclnBootstrapData?.searchRequest}
              ctaTitle={listingsLoadingMessage}
              products={isPackageListings ? bundleComponents.map((c) => c.type) : undefined}
              isShelves={pclnBootstrapData?.isShelves}
              isNotReshopAndWLAndPackages={pclnBootstrapData?.isNotReshopAndWLAndPackages}
            />
          }
        >
          <AirSearchPromiseResolver>
            <ListingsContentContainer
              forceHeaderOpen={forceHeaderOpen}
              priceWatchState={priceWatchState}
              isPackageListings={isPackageListings}
              ahCrossSellMaxShowCount={ahCrossSellMaxShowCount}
              isHorizonListing={isHorizonListing}
              packageType={packageType}
              setPriceWatchState={setPriceWatchState}
              setSelectedDepartureSummaries={setSelectedDepartureSummaries}
              selectedDepartureSummaries={selectedDepartureSummaries}
              handleTabChange={handleTabChange}
              theme={theme}
            />
          </AirSearchPromiseResolver>
        </Suspense>
      </Middle>
    </ListingsBody>
  )
}

export { ListingsPageV2 }
